import React, { useEffect, useState, useCallback } from "react";
import * as Mui from "@mui/material";
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  MenuItem,
  Select,
  Button,
  Chip,
  CircularProgress,
  Avatar,
  Collapse,
  Divider,
  Paper,
  Grid,
  Stack,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Menu,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Call, RestartAlt } from "@mui/icons-material";
import {
  ViewModule,
  TableChart,
  MoreVert,
  CheckCircle,
} from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { tooltipStyleProps } from "services/constants/Styles";
import { Person, ArrowForward, Hail } from "@mui/icons-material";
import BSDateRangePicker from "pages/MyZone/Status/DateRangePicker";
import Badge from "@mui/material/Badge";
import ReminderForm from "components/contacts/ReminderForm";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import CallIcon from "@mui/icons-material/Call";
import CallInterface from "components/contacts/CallInterface";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import ContactDetails from "components/contacts/ContactDetails";

dayjs.extend(relativeTime);

const fallbackAvatar = "https://cdn-icons-png.flaticon.com/512/149/149071.png";

export default function ReminderSummary() {
  const { domain, token, userDetails } = useSelector(
    (s) => s.tokenReducer || {}
  );
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [showCallInterface, setShowCallInterface] = useState(false);
  const [rawReminders, setRawReminders] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedMap, setExpandedMap] = useState({});
  const [contacts, setContacts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState("");
  const [reminderTypes, setReminderTypes] = useState([]);
  const [dateRangeTemp, setDateRangeTemp] = useState({ from: "", to: "" });
  const [viewMode, setViewMode] = useState("card");
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [reminderFormContactId, setReminderFormContactId] = useState(null);
  const [editingReminder, setEditingReminder] = useState(null);
  const [selectedReminderId, setSelectedReminderId] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [sortValue, setSortValue] = useState("date_desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dateRangeApplied, setDateRangeApplied] = useState({
    from: "",
    to: "",
  });

  const defaultFilters = {
    search: "",
    status: "All",
    priority: "All Priorities",
    type: "All Types",
    contact: "All Contacts",
    agent: userDetails?.id || "All Agents",
    fromDate: "",
    toDate: "",
  };
  const [filters, setFilters] = useState(defaultFilters);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    reminder: null,
  });

  const fetchReminderTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${domain}crm/reminder-type/`, {
        headers: {
          Authorization: `token ${token}`,
        },
      });
      setReminderTypes(
        Array.isArray(res.data) ? res.data : res.data?.results || []
      );
    } catch (err) {
      console.error("Failed to fetch reminder types:", err);
    }
  }, [domain, token]);

  useEffect(() => {
    fetchReminderTypes();
  }, [fetchReminderTypes]);
  const fetchReminders = useCallback(
    async (params = {}) => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${domain}crm/reminders/`, {
          headers: {
            Authorization: `token ${token}`,
          },
          params,
        });

        const data = Array.isArray(res.data)
          ? res.data
          : res.data?.results || [];
        setReminders(data);
        setRawReminders(data);
      } catch (err) {
        console.error("Failed to fetch reminders:", err);
        setError("Failed to load reminders");
      } finally {
        setLoading(false);
      }
    },
    [domain, token]
  );

  const fetchContacts = useCallback(async () => {
    try {
      const res = await axios.get(`${domain}crm/contacts/`, {
        headers: {
          Authorization: `token ${token}`,
        },
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setContacts(data);
    } catch (err) {
      console.error("Failed to fetch contacts:", err);
    }
  }, [domain, token]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await axios.get(
        `${domain}crm/product-based/user/?product=CRM`,
        {
          headers: {
            Authorization: `token ${token}`,
          },
        }
      );
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setAgents(data);
    } catch (err) {
      console.error("Failed to fetch agents:", err);
    }
  }, [domain, token]);

  useEffect(() => {
    const from = dayjs().subtract(6, "day").format("DD/MM/YYYY"); // 7 days including today
    const to = dayjs().format("DD/MM/YYYY");

    setDateRangeTemp({ from, to });
    setDateRangeApplied({ from, to });

    const params = {
      start_date: dayjs(from, "DD/MM/YYYY").format("YYYY-MM-DD"),
      end_date: dayjs(to, "DD/MM/YYYY").format("YYYY-MM-DD"),
    };
    if (userDetails?.id) {
      params.reminder_assigned_to = userDetails.id;
    }
    fetchReminders(params);
    fetchContacts();
    fetchAgents();
  }, [fetchReminders, fetchContacts, fetchAgents, userDetails?.id]);

  useEffect(() => {
    const mapped = rawReminders.map((r) => {
      const agent = agents.find((a) => a.username === r.agent_name);
      const assigned = agents.find((a) => a.id === r.assigned_to);
      const contact = contacts.find((c) => c.id === r.contact);

      return {
        ...r,
        agent_with_avatar: agent || {},
        assigned_to_with_avatar: assigned || {},
        contact_with_avatar: contact || {},
      };
    });
    setReminders(mapped);
  }, [rawReminders, agents, contacts]);

  // Sorting
  const handleSort = (key) => {
    if (key === "time" || key === "notes") return; // block
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setSortValue(`${key}_${direction}`); // keeps dropdown in sync
  };

  const sortReminders = (data, { key, direction }) => {
    const order = direction === "asc" ? 1 : -1;
    return [...data].sort((a, b) => {
      switch (key) {
        case "title":
          return (a.title || "").localeCompare(b.title || "") * order;
        case "priority":
          return (a.priority || "").localeCompare(b.priority || "") * order;
        case "status":
          return (a.status || "").localeCompare(b.status || "") * order;
        case "agent":
          return (a.agent_name || "").localeCompare(b.agent_name || "") * order;
        case "contact":
          return (
            (a.contact_name || "").localeCompare(b.contact_name || "") * order
          );
        case "date":
          return (new Date(a.date) - new Date(b.date)) * order;
        case "assigned_to":
          return (a.username || "").localeCompare(b.username || "") * order;
        default:
          return 0;
      }
    });
  };
  const globalSearchValue = useSelector(
    (state) => state.tokenReducer.globalSearchValue || ""
  );

  const filteredReminders = reminders.filter((r) => {
    const term = (globalSearchValue || "").toLowerCase();
    if (!term) return true;
    return (
      r.title?.toLowerCase().includes(term) ||
      r.priority?.toLowerCase().includes(term) ||
      r.status?.toLowerCase().includes(term) ||
      r.contact_name?.toLowerCase().includes(term) ||
      r.contact_company_name?.toLowerCase().includes(term) ||
      r.agent_name?.toLowerCase().includes(term) ||
      r.notes?.toLowerCase().includes(term) ||
      r.assigned_to_name?.toLowerCase().includes(term)
    );
  });

  const sortedReminders = sortReminders(filteredReminders, sortConfig);

  const paginatedReminders = sortedReminders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleExpand = (id) => {
    setExpandedMap((prev) => {
      const newMap = {};
      newMap[id] = !prev[id];
      return newMap;
    });
  };
  useEffect(() => {
    setExpandedMap({});
  }, [viewMode]);

  const handleApplyFilters = async () => {
    setFiltersChanged(true);
    const params = {};

    if (filters.search) params.search = filters.search;
    if (filters.status && filters.status !== "All") {
      params.status = filters.status;
    }
    if (filters.priority && filters.priority !== "All Priorities")
      params.priority = filters.priority;
    if (filters.type && filters.type !== "All Types") {
      params.type = filters.type;
    }

    if (filters.contact && filters.contact !== "All Contacts")
      params.contact_id = filters.contact;
    if (filters.agent && filters.agent !== "All Agents")
      params.reminder_assigned_to = filters.agent;

    if (dateRangeTemp.from && dateRangeTemp.to) {
      params.start_date = dayjs(dateRangeTemp.from, "DD/MM/YYYY").format(
        "YYYY-MM-DD"
      );
      params.end_date = dayjs(dateRangeTemp.to, "DD/MM/YYYY").format(
        "YYYY-MM-DD"
      );
    }
    setDateRangeApplied({ ...dateRangeTemp });
    await fetchReminders(params);
    setDrawerOpen(false);
  };

  useEffect(() => {
    if (drawerOpen) {
      if (dateRangeApplied.from && dateRangeApplied.to) {
        // Show applied date range
        setDateRangeTemp({ ...dateRangeApplied });
      } else {
        // Empty on fresh open
        setDateRangeTemp({ from: "", to: "" });
      }
    }
  }, [drawerOpen, dateRangeApplied]);

  const handleResetFilters = async () => {
    setFiltersChanged(false);
    setFilters({
      search: "",
      status: "All",
      priority: "All Priorities",
      type: "All Types",
      contact: "All Contacts",
      agent: userDetails?.id || "All Agents",
      fromDate: "",
      toDate: "",
    });
    setDateRangeApplied({ from: "", to: "" });
    setDateRangeTemp({ from: "", to: "" });
    const from = dayjs().subtract(6, "day").format("DD/MM/YYYY"); // 7 days including today
    const to = dayjs().format("DD/MM/YYYY");

    setDateRangeTemp({ from, to });
    setDateRangeApplied({ from, to });

    const params = {
      start_date: dayjs(from, "DD/MM/YYYY").format("YYYY-MM-DD"),
      end_date: dayjs(to, "DD/MM/YYYY").format("YYYY-MM-DD"),
    };
    if (userDetails?.id) {
      params.reminder_assigned_to = userDetails.id;
    }
    await fetchReminders(params);
    setDrawerOpen(false);
  };

  const formatMonth = (d) => (d ? dayjs(d).format("MMM") : "");
  const formatDay = (d) => (d ? dayjs(d).format("D") : "");
  const formatTime = (d) => (d ? dayjs(d).format("h:mm A") : "");

  const handleCallClick = async (contactId) => {
    try {
      const res = await axios.get(`${domain}crm/contacts/${contactId}/`, {
        headers: {
          Authorization: `token ${token}`,
        },
      });
      setSelectedReminder({ contact: res.data });
      setShowCallInterface(true);
    } catch (err) {
      console.error("Failed to fetch contact:", err);
      setError("Unable to fetch contact details");
    }
  };

  const handleCompleteReminder = async (reminderId, actionPoint) => {
    setLoading(true);
    setError("");
    try {
      await axios.put(
        `${domain}crm/reminders/${reminderId}/complete/`,
        { action_point: actionPoint },
        {
          headers: {
            Authorization: `token ${token}`,
          },
        }
      );
      fetchReminders(); // refresh list
    } catch (err) {
      console.error("Failed to complete reminder:", err);
      setError("Unable to complete reminder");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteAndCreateAnother = async (reminderObj, actionPoint) => {
    await handleCompleteReminder(reminderObj.id, actionPoint);
    setReminderFormContactId(reminderObj.contact); // reminder has contact id in your data
    setEditingReminder(null);
    setShowReminderForm(true);
  };

  const filterCount = (() => {
    let count = 0;
    Object.entries(filters).forEach(([key, val]) => {
      if (!val) return;
      if (["priority", "contact", "agent", "status"].includes(key)) {
        if (
          ["All Priorities", "All Contacts", "All Agents", "All"].includes(val)
        )
          return;
      }
      if (key === "fromDate" || key === "toDate") return;
      count++;
    });
    if (filters.fromDate || filters.toDate) count++;
    return count;
  })();
  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 250,
        overflowY: "auto",
        overflowX: "auto",
      },
    },
  };

  const filteredContacts =
    filters.agent && filters.agent !== "All Agents"
      ? contacts.filter((c) => String(c.assigned_to) === String(filters.agent))
      : contacts;

  const formatStatus = (status) => {
    switch (status) {
      case "Overdue":
        return { label: "Overdue", color: "error" };
      case "Completed":
        return { label: "Completed", color: "success" };
      case "Pending":
      default:
        return { label: "Pending", color: "info" };
    }
  };

  const overdueReminders = reminders.filter(
    (r) =>
      dayjs(r.date).isBefore(dayjs()) &&
      !r.action_point &&
      r.status !== "completed"
  );

  const overdueCount = overdueReminders.length;
  return (
    <Box p={2}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5">Reminders</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {/* Filter Button */}
          <Badge
            badgeContent={filtersChanged ? filterCount : 0}
            color="primary"
          >
            <IconButton
              aria-label="filters"
              onClick={() => setDrawerOpen(true)}
            >
              <Tooltip slotProps={tooltipStyleProps} title={"Filters"} arrow>
                <FilterListIcon />
              </Tooltip>
            </IconButton>
          </Badge>

          {/* Sort Dropdown */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortValue}
              MenuProps={menuProps}
              label="Sort By"
              onChange={(e) => {
                const value = e.target.value;
                const lastUnderscore = value.lastIndexOf("_");
                const key = value.substring(0, lastUnderscore);
                const direction = value.substring(lastUnderscore + 1);
                setSortValue(value);
                setSortConfig({ key, direction });
              }}
            >
              <MenuItem value="date_desc">Date (Newest)</MenuItem>
              <MenuItem value="date_asc">Date (Oldest)</MenuItem>
              <MenuItem value="title_asc">Title (A–Z)</MenuItem>
              <MenuItem value="title_desc">Title (Z–A)</MenuItem>
              <MenuItem value="priority_asc">Priority (A–Z)</MenuItem>
              <MenuItem value="priority_desc">Priority (Z–A)</MenuItem>
              <MenuItem value="status_asc">Status (A–Z)</MenuItem>
              <MenuItem value="status_desc">Status (Z–A)</MenuItem>
              <MenuItem value="agent_asc">Agent (A–Z)</MenuItem>
              <MenuItem value="agent_desc">Agent (Z–A)</MenuItem>
              <MenuItem value="contact_asc">Contact (A–Z)</MenuItem>
              <MenuItem value="contact_desc">Contact (Z–A)</MenuItem>
              <MenuItem value="assigned_to_asc">Assigned To (A–Z)</MenuItem>
              <MenuItem value="assigned_to_desc">Assigned To (Z–A)</MenuItem>
            </Select>
          </FormControl>
          {/* Toggle Button Group */}
          <Mui.ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, val) => val && setViewMode(val)}
            size="small"
          >
            <Tooltip arrow slotProps={tooltipStyleProps} title="Card View">
              <Mui.ToggleButton value="card">
                <ViewModule fontSize="small" />
              </Mui.ToggleButton>
            </Tooltip>
            <Tooltip arrow slotProps={tooltipStyleProps} title="Table View">
              <Mui.ToggleButton value="table">
                <TableChart fontSize="small" />
              </Mui.ToggleButton>
            </Tooltip>
          </Mui.ToggleButtonGroup>

          {/* Refresh Button */}
          <Tooltip arrow slotProps={tooltipStyleProps} title="Reset Filters">
            <IconButton
              aria-label="refresh"
              onClick={() => {
                handleResetFilters();
              }}
            >
              <RestartAlt />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Typography color="error" mb={1}>
          {error}
        </Typography>
      )}

      {overdueCount > 0 && (
        <Box mb={2} display="flex" alignItems="center" gap={1}>
          <Chip
            label={`Overdue Reminders: ${overdueCount}`}
            color="error"
            variant="filled"
          />
        </Box>
      )}

      {/* List of reminders - compact by default, expand for details */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : sortedReminders.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Hail fontSize="large" sx={{ color: "gray" }} />
          <Typography variant="h6">No reminders found</Typography>
          <Typography variant="body2" color="text.secondary">
            {globalSearchValue
              ? "Try adjusting your search"
              : "Get started by adding your first reminder"}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={selectedContact ? 8 : 12}>
            {viewMode === "card" ? (
              <>
                <Stack spacing={2}>
                  {/* <Grid item xs={12} md={selectedContact ? 8 : 12}> */}
                  {paginatedReminders.map((rem) => {
                    const id = rem.id;
                    const isExpanded = !!expandedMap[id];
                    const contactName =
                      rem.contact_company_name || rem.contact_name;
                    const agentName = rem.agent_name;

                    return (
                      <Paper
                        key={id}
                        variant="outlined"
                        onClick={() => {
                          setSelectedReminderId(rem.id);
                          const contactObj = contacts.find(
                            (c) => c.id === rem.contact
                          );
                          setSelectedContact(contactObj || null);
                        }}
                        sx={{
                          cursor: "pointer",
                          border:
                            selectedReminderId === rem.id
                              ? "2px solid #1976d2"
                              : "1px solid #e0e0e0",
                          backgroundColor:
                            selectedReminderId === rem.id
                              ? "rgba(25,118,210,0.08)"
                              : "white",
                          boxShadow:
                            selectedReminderId === rem.id
                              ? "0 4px 16px rgba(25,118,210,0.15)"
                              : "0 2px 8px rgba(0,0,0,0.06)",
                          position: "relative",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            height: "100%",
                            width: "4px",
                            backgroundColor:
                              selectedReminderId === rem.id
                                ? "#1976d2"
                                : "transparent",
                            borderTopLeftRadius: "inherit",
                            borderBottomLeftRadius: "inherit",
                          },
                        }}
                      >
                        <Box display="flex" alignItems="center" px={2} py={1}>
                          {/* Left date */}
                          <Box
                            sx={{
                              width: 72,
                              textAlign: "center",
                              pr: 2,
                              borderRight: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Tooltip
                              arrow
                              slotProps={tooltipStyleProps}
                              title="Due Date"
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatMonth(rem.date)}
                              </Typography>
                              <Typography variant="h5" fontWeight={700}>
                                {formatDay(rem.date)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatTime(rem.date)}
                              </Typography>
                            </Tooltip>
                          </Box>

                          {/* Middle summary */}
                          <Box flex={1} px={2}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={600}
                              noWrap
                            >
                              <Tooltip
                                arrow
                                slotProps={tooltipStyleProps}
                                title="Title"
                              >
                                {rem.title}
                              </Tooltip>
                            </Typography>

                            {/* Chips with gap */}
                            <Box
                              display="flex"
                              flexWrap="wrap"
                              gap={1}
                              mt={0.5}
                            >
                              {rem.priority && (
                                <Tooltip
                                  arrow
                                  slotProps={tooltipStyleProps}
                                  title="Priority"
                                >
                                  <Chip
                                    label={rem.priority}
                                    size="small"
                                    color={
                                      rem.priority
                                        ?.toLowerCase()
                                        .includes("high")
                                        ? "error"
                                        : "default"
                                    }
                                  />
                                </Tooltip>
                              )}

                              {/* {rem.status && (
                          <Chip
                            label={rem.status}
                            size="small"
                            color="warning"
                          />
                        )} */}

                              {rem.type && (
                                <Tooltip
                                  arrow
                                  slotProps={tooltipStyleProps}
                                  title="Type"
                                >
                                  <Chip
                                    label={rem.reminder_name}
                                    size="small"
                                    sx={{
                                      backgroundColor:
                                        rem.status_color || "default",
                                      color: "#fff",
                                    }}
                                  />
                                </Tooltip>
                              )}

                              {rem.date && (
                                <Tooltip
                                  arrow
                                  slotProps={tooltipStyleProps}
                                  title="Reminder was created"
                                >
                                  <Chip
                                    label={dayjs(rem.date).fromNow()}
                                    size="small"
                                    icon={<SearchIcon />}
                                  />
                                </Tooltip>
                              )}

                              {rem.assigned_to && (
                                <Tooltip
                                  arrow
                                  slotProps={tooltipStyleProps}
                                  title="Reminder Assigned To"
                                >
                                  <Chip
                                    label={rem.username}
                                    size="small"
                                    avatar={
                                      <Avatar
                                        src={
                                          rem.assigned_to_with_avatar
                                            ?.image_url || fallbackAvatar
                                        }
                                        sx={{ width: 20, height: 20 }}
                                      />
                                    }
                                  />
                                </Tooltip>
                              )}
                              {rem.date && (
                                <Tooltip
                                  arrow
                                  slotProps={tooltipStyleProps}
                                  title="Status"
                                >
                                  <Chip
                                    label={formatStatus(rem.status).label}
                                    size="small"
                                    color={formatStatus(rem.status).color}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </Box>

                          {/* Call Icon with tooltip & adjusted size */}
                          <Box display="flex" alignItems="center" gap={2}>
                            {/* Call Icon */}

                            <Tooltip
                              arrow
                              slotProps={tooltipStyleProps}
                              title="Make Call"
                            >
                              <IconButton
                                color="primary"
                                size="small"
                                aria-label="Make Call"
                                onClick={() => handleCallClick(rem.contact)}
                              >
                                <CallIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {/* Agent Name (Tooltip, not link) */}
                            <Tooltip
                              arrow
                              slotProps={tooltipStyleProps}
                              title="Agent"
                            >
                              <Box
                                display="inline-flex"
                                alignItems="center"
                                sx={{
                                  color: "#2563EB",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  cursor: "default",
                                }}
                              >
                                <Hail sx={{ fontSize: 16, mr: 0.3 }} />
                                {agentName}
                              </Box>
                            </Tooltip>

                            {/* Contact Link */}
                            <Tooltip
                              arrow
                              slotProps={tooltipStyleProps}
                              title="Contact Details"
                            >
                              {/* <Link
                          to={{
                            pathname: "/admin/crm/contacts",
                            state: { contactId: rem.contact },
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            color: "#2563EB",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            textDecoration: "none",
                          }}
                        >
                          <Person sx={{ fontSize: 16, mr: 0.3 }} />
                          {contactName}
                          <ArrowForward sx={{ fontSize: 14, ml: 0.2 }} />
                        </Link> */}
                              <Box
                                onClick={() => {
                                  const contactObj = contacts.find(
                                    (c) => c.id === rem.contact
                                  );
                                  setSelectedContact(contactObj || null);
                                }}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  color: "#2563EB",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  cursor: "pointer",
                                }}
                              >
                                <Person sx={{ fontSize: 16, mr: 0.3 }} />
                                {contactName}
                                <ArrowForward sx={{ fontSize: 14, ml: 0.2 }} />
                              </Box>
                            </Tooltip>
                          </Box>

                          {/* Expand/Collapse Icon */}
                          <IconButton
                            onClick={() => toggleExpand(id)}
                            aria-label={isExpanded ? "collapse" : "expand"}
                          >
                            {isExpanded ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </Box>

                        {/* Expanded details area */}
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Divider />
                          <Box
                            px={3}
                            py={2}
                            display="flex"
                            flexDirection="column"
                            gap={2}
                          >
                            {/* Details Row */}
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              gap={2}
                            >
                              <Box flex={1}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  Details
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {rem.notes ||
                                    rem.full_description ||
                                    "No additional details"}
                                </Typography>
                              </Box>

                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() =>
                                  setActionDialog({ open: true, reminder: rem })
                                }
                                sx={{
                                  borderRadius: 2,
                                  textTransform: "none",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Mark as Completed
                              </Button>
                            </Box>

                            {/* Action Point (if available) */}
                            {rem.action_point && (
                              <Box
                                p={1.5}
                                bgcolor="grey.50"
                                border="1px solid"
                                borderColor="divider"
                                borderRadius={2}
                              >
                                <Typography
                                  variant="subtitle2"
                                  color="success.main"
                                  gutterBottom
                                >
                                  Action Point
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.primary"
                                >
                                  {rem.action_point} - Completed by{" "}
                                  {rem.modifiedby}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}
                  {/* </Grid> */}
                </Stack>
                {sortedReminders.length > rowsPerPage && (
                  <Mui.TablePagination
                    component="div"
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    count={sortedReminders.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ mt: 2 }}
                  />
                )}
              </>
            ) : (
              <ReminderTable
                data={sortedReminders}
                page={page}
                rowsPerPage={rowsPerPage}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handleSort={handleSort}
                sortConfig={sortConfig}
                onRowClick={(rem) => {
                  setSelectedReminderId(rem.id);
                  const contactObj = contacts.find((c) => c.id === rem.contact);
                  setSelectedContact(contactObj || null);
                }}
                selectedReminderId={selectedReminderId}
                handleCallClick={handleCallClick}
                handleCompleteReminder={handleCompleteReminder}
                handleCompleteAndCreateAnother={handleCompleteAndCreateAnother}
                loading={loading}
              />
            )}
          </Grid>
          {selectedContact && (
            <Grid item xs={12} md={4}>
              <ContactDetails
                contact={selectedContact}
                onClose={() => setSelectedContact(null)}
                onUpdateContact={(updatedContact) => {
                  if (updatedContact && updatedContact.id) {
                    setContacts((prev) =>
                      prev.map((c) =>
                        c.id === updatedContact.id ? updatedContact : c
                      )
                    );
                    setSelectedContact(updatedContact);
                  }
                }}
              />
            </Grid>
          )}
        </Grid>
      )}
      {showCallInterface && selectedReminder && (
        <CallInterface
          contact={selectedReminder.contact}
          open={showCallInterface}
          onClose={() => {
            setShowCallInterface(false);
            setSelectedReminder(null);
          }}
        />
      )}
      {actionDialog.open && (
        <CompleteReminderDialog
          open={actionDialog.open}
          reminder={actionDialog.reminder}
          onClose={() => setActionDialog({ open: false, reminder: null })}
          onSubmit={handleCompleteReminder}
          loading={loading}
          onCompleteAndCreateAnother={handleCompleteAndCreateAnother}
        />
      )}
      {showReminderForm && (
        <ReminderForm
          contactId={reminderFormContactId}
          reminder={editingReminder} // null => create
          onSave={() => {
            setShowReminderForm(false);
            setReminderFormContactId(null);
            setEditingReminder(null);
            fetchReminders();
          }}
          onCancel={() => {
            setShowReminderForm(false);
            setReminderFormContactId(null);
            setEditingReminder(null);
          }}
        />
      )}
      {/* {selectedContact && (
        <Box mt={3}>
          <ContactDetails contact={selectedContact} />
        </Box>
      )} */}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={(event, reason) => {
          // Prevent closing on outside click or ESC
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setDrawerOpen(false);
        }}
      >
        <Box
          width={360}
          p={3}
          display="flex"
          flexDirection="column"
          height="100%"
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h6">Advanced Filters</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {/* Status & Priority */}
            <Box display="flex" gap={2} mb={2}>
              <Box flex={1}>
                <Typography variant="body2" fontWeight={500} mb={0.5}>
                  Status
                </Typography>
                <Select
                  fullWidth
                  size="medium"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((s) => ({ ...s, status: e.target.value }))
                  }
                  sx={{
                    borderRadius: "12px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "12px",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              </Box>
              <Box flex={1}>
                <Typography variant="body2" fontWeight={500} mb={0.5}>
                  Priority
                </Typography>
                <Select
                  fullWidth
                  size="medium"
                  value={filters.priority}
                  onChange={(e) =>
                    setFilters((s) => ({ ...s, priority: e.target.value }))
                  }
                  sx={{
                    borderRadius: "12px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "12px",
                    },
                  }}
                >
                  <MenuItem value="All Priorities">All Priorities</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </Box>
            </Box>

            {/* Reminder Type */}
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Reminder Type
            </Typography>
            <Select
              fullWidth
              size="medium"
              value={filters.type}
              onChange={(e) =>
                setFilters((s) => ({ ...s, type: e.target.value }))
              }
              sx={{
                borderRadius: "12px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "12px",
                },
              }}
            >
              <MenuItem value="All Types">All Types</MenuItem>
              {reminderTypes.map((rt) => (
                <MenuItem key={rt.id} value={String(rt.id)}>
                  {rt.label}
                </MenuItem>
              ))}
            </Select>

            {/* Agent */}
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Agent
            </Typography>
            <Select
              MenuProps={menuProps}
              fullWidth
              size="medium"
              value={filters.agent}
              onChange={(e) =>
                setFilters((s) => ({ ...s, agent: e.target.value }))
              }
              renderValue={(val) => {
                if (val === "All Agents") {
                  return (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        src={fallbackAvatar}
                        sx={{ width: 24, height: 24 }}
                      >
                        A
                      </Avatar>{" "}
                      All Agents
                    </Box>
                  );
                }
                const u = agents.find((x) => String(x.id) === String(val));
                return u ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar src={u.image_url} sx={{ width: 24, height: 24 }} />{" "}
                    {u.username}
                  </Box>
                ) : (
                  "All Agents"
                );
              }}
              sx={{
                borderRadius: "12px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "12px",
                },
              }}
            >
              <MenuItem value="All Agents">
                <ListItemIcon>
                  <Avatar src={fallbackAvatar} sx={{ width: 28, height: 28 }} />
                </ListItemIcon>
                <ListItemText primary="All Agents" />
              </MenuItem>
              {agents.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  <ListItemIcon>
                    <Avatar src={u.image_url} sx={{ width: 28, height: 28 }} />
                  </ListItemIcon>
                  <ListItemText primary={u.username} />
                </MenuItem>
              ))}
            </Select>

            {/* Contact */}
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Contact
            </Typography>
            <Select
              MenuProps={menuProps}
              fullWidth
              size="medium"
              value={filters.contact}
              onChange={(e) =>
                setFilters((s) => ({ ...s, contact: e.target.value }))
              }
              renderValue={(val) => {
                if (val === "All Contacts") {
                  return (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        src={fallbackAvatar}
                        sx={{ width: 24, height: 24 }}
                      >
                        C
                      </Avatar>{" "}
                      All Contacts
                    </Box>
                  );
                }
                const c = contacts.find((x) => String(x.id) === String(val));
                return c ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar src={fallbackAvatar} sx={{ width: 24, height: 24 }}>
                      C
                    </Avatar>
                    {c.company ||
                      c.name ||
                      `${c.first_name || ""} ${c.last_name || ""}`}
                  </Box>
                ) : (
                  "All Contacts"
                );
              }}
              sx={{
                borderRadius: "12px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "12px",
                },
              }}
            >
              <MenuItem value="All Contacts">
                <ListItemIcon>
                  <Avatar src={fallbackAvatar} sx={{ width: 24, height: 24 }} />
                </ListItemIcon>
                <ListItemText primary="All Contacts" />
              </MenuItem>
              {filteredContacts.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  <ListItemIcon>
                    <Avatar
                      src={fallbackAvatar}
                      sx={{ width: 24, height: 24 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      noWrap: true,
                      style: {
                        maxWidth: 180, // cap width
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                    primary={
                      c.company ||
                      c.name ||
                      `${c.first_name || ""} ${c.last_name || ""}`
                    }
                  />
                </MenuItem>
              ))}
            </Select>

            {/* Date Range */}
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Date Range
            </Typography>
            <BSDateRangePicker
              showToday
              triggerDate={true}
              includeCurrentDate
              fromDate={dateRangeTemp.from}
              toDate={dateRangeTemp.to}
              allowFutureDate={true}
              handleStartDate={(start, end) => {
                setDateRangeTemp({ from: start, to: end });
              }}
            />

            {/* Spacer */}
            {/* <Box flex={1} /> */}

            {/* Buttons */}
            <Box display="flex" gap={1} mt={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<RefreshIcon />}
                onClick={handleResetFilters}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleApplyFilters}
                sx={{
                  backgroundColor: "#84DEB8",
                  color: "#000",
                  "&:hover": { backgroundColor: "#6cc9a1" },
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

const ReminderTable = ({
  data,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  handleSort,
  sortConfig,
  onRowClick,
  selectedReminderId,
  handleCallClick,
  handleCompleteReminder,
  handleCompleteAndCreateAnother,
  loading,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    reminderId: null,
    value: "",
  });

  const formatStatus = (status) => {
    switch (status) {
      case "Overdue":
        return { label: "Overdue", color: "error" };
      case "Completed":
        return { label: "Completed", color: "success" };
      case "Pending":
      default:
        return { label: "Pending", color: "info" };
    }
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  return (
    <Paper>
      <Mui.TableContainer>
        <Mui.Table>
          <Mui.TableHead>
            <Mui.TableRow>
              {[
                { key: "date", label: "Date" },
                { key: "time", label: "Time", disableSort: true },
                { key: "title", label: "Title" },
                { key: "priority", label: "Priority" },
                { key: "status", label: "Status" },
                { key: "notes", label: "Notes", disableSort: true },
                { key: "agent", label: "Agent" },
                { key: "contact", label: "Contact" },
                { key: "assigned_to", label: "Assigned To" },
                { key: "actions", label: "Actions" },
              ].map((col) => (
                <Mui.TableCell
                  key={col.key}
                  onClick={() => !col.disableSort && handleSort(col.key)}
                  sx={{
                    cursor: !col.disableSort ? "pointer" : "default",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {col.label}
                  {!col.disableSort && sortConfig.key === col.key && (
                    <Box component="span" ml={0.5}>
                      {sortConfig.direction === "asc" ? "↑" : "↓"}
                    </Box>
                  )}
                </Mui.TableCell>
              ))}
            </Mui.TableRow>
          </Mui.TableHead>

          <Mui.TableBody>
            {data
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((rem) => {
                const statusInfo = formatStatus(rem.status);
                return (
                  <Mui.TableRow
                    key={rem.id}
                    hover
                    onClick={() => onRowClick(rem)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        selectedReminderId === rem.id
                          ? "rgba(25,118,210,0.08)"
                          : "white",
                      "&:hover": {
                        backgroundColor:
                          selectedReminderId === rem.id
                            ? "rgba(25,118,210,0.12)"
                            : "rgba(0,0,0,0.04)",
                      },
                      borderLeft:
                        selectedReminderId === rem.id
                          ? "4px solid #1976d2"
                          : "4px solid transparent",
                    }}
                  >
                    {/* Date & Time */}
                    <Mui.TableCell>
                      {dayjs(rem.date).format("DD-MM-YYYY")}
                    </Mui.TableCell>
                    <Mui.TableCell>
                      {dayjs(rem.date).format("HH:mm")}
                    </Mui.TableCell>

                    {/* Title */}
                    <Mui.TableCell>{rem.title}</Mui.TableCell>

                    {/* Priority */}
                    <Mui.TableCell>
                      <Chip
                        size="small"
                        label={rem.priority}
                        color={
                          rem.priority?.toLowerCase() === "high"
                            ? "error"
                            : "default"
                        }
                      />
                    </Mui.TableCell>

                    {/* Status */}
                    <Mui.TableCell>
                      <Chip
                        size="small"
                        label={statusInfo.label}
                        color={statusInfo.color}
                      />
                    </Mui.TableCell>

                    {/* Notes with tooltip */}
                    <Mui.TableCell
                      sx={{
                        maxWidth: 200,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <Mui.Tooltip title={rem.notes || "No notes"}>
                        <span>{rem.notes || "—"}</span>
                      </Mui.Tooltip>
                    </Mui.TableCell>

                    {/* Agent */}
                    <Mui.TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={
                            rem.agent_with_avatar?.image_url || fallbackAvatar
                          }
                          sx={{ width: 24, height: 24 }}
                        />
                        {rem.agent_name}
                      </Box>
                    </Mui.TableCell>

                    {/* Contact */}
                    <Mui.TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={
                            rem.contact_with_avatar?.image_url || fallbackAvatar
                          }
                          sx={{ width: 24, height: 24 }}
                        />
                        {rem.contact_company_name || rem.contact_name}
                      </Box>
                    </Mui.TableCell>

                    {/* Assigned To */}
                    <Mui.TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={
                            rem.assigned_to_with_avatar?.image_url ||
                            fallbackAvatar
                          }
                          sx={{ width: 24, height: 24 }}
                        />
                        {rem.username}
                      </Box>
                    </Mui.TableCell>

                    {/* Actions */}
                    <Mui.TableCell>
                      <IconButton onClick={(e) => handleMenuOpen(e, rem)}>
                        <MoreVert />
                      </IconButton>
                    </Mui.TableCell>
                  </Mui.TableRow>
                );
              })}
          </Mui.TableBody>
        </Mui.Table>
      </Mui.TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (menuRow) {
              onRowClick(menuRow);
            }
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuRow) handleCallClick(menuRow.contact);
            handleMenuClose();
          }}
          // onClick={() => onCall(menuRow.contact)}
        >
          <ListItemIcon>
            <Call fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Call" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuRow) {
              setActionDialog({
                open: true,
                reminder: menuRow,
              });
            }
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <CheckCircle fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Complete" />
        </MenuItem>
      </Menu>

      {actionDialog.open && (
        <CompleteReminderDialog
          open={actionDialog.open}
          reminder={actionDialog.reminder}
          onClose={() => setActionDialog({ open: false, reminder: null })}
          onSubmit={handleCompleteReminder}
          loading={loading}
          onCompleteAndCreateAnother={handleCompleteAndCreateAnother}
        />
      )}

      {data.length > rowsPerPage && (
        <Mui.TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Paper>
  );
};

const CompleteReminderDialog = ({
  open,
  onClose,
  onSubmit,
  onCompleteAndCreateAnother,
  reminder,
  loading,
}) => {
  const [actionPoint, setActionPoint] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (reminder) {
      setActionPoint(reminder.action_point || "");
      setError("");
    }
  }, [reminder]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (
          reason &&
          (reason === "backdropClick" || reason === "escapeKeyDown")
        )
          return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Complete Reminder
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Please provide an <strong>Action Point</strong> before marking this
          reminder as completed.
        </Typography>
        <Mui.TextField
          label="Action Point"
          fullWidth
          multiline
          minRows={2}
          value={actionPoint}
          onChange={(e) => {
            setActionPoint(e.target.value);
            if (e.target.value.trim()) setError("");
          }}
          error={Boolean(error)}
          helperText={error}
        />
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#84DEB8",
            color: "#000",
            "&:hover": { backgroundColor: "#6cc9a1" },
          }}
          disabled={loading}
          onClick={() => {
            if (!actionPoint.trim()) {
              setError("Action point is required!");
              return;
            }
            onSubmit(reminder.id, actionPoint);
            onClose();
          }}
        >
          {loading ? <CircularProgress size={20} /> : "Complete"}
        </Button>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#84DEB8",
            color: "#000",
            "&:hover": { backgroundColor: "#6cc9a1" },
          }}
          disabled={loading}
          onClick={() => {
            if (!actionPoint.trim()) {
              setError("Action point is required!");
              return;
            }
            onCompleteAndCreateAnother?.(reminder, actionPoint);
            onClose();
          }}
        >
          Complete & Create Another
        </Button>
      </DialogActions>
    </Dialog>
  );
};
